import React, { useEffect, useState } from 'react';
import { Modal, Button, Alert, Spinner, Table, Badge } from '../../../../core/ui';
import { previewInstallmentDuplicates, applyInstallmentDedup } from '../../api/budget';
import { formatTry } from '../../format';
import { useTranslation } from '../../../../core/context/TranslationContext';
import styles from './DuplicatesModal.module.css';

const DuplicatesModal = ({ show, onHide, onApplied }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!show) {
      setReport(null);
      setSummary(null);
      setError(null);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const r = await previewInstallmentDuplicates();
        setReport(r);
      } catch (err) {
        setError(err?.message || 'error');
      } finally {
        setLoading(false);
      }
    })();
  }, [show]);

  const reasonLabel = (reason) => {
    switch (reason) {
      case 'user-edited': return t('budget.duplicatesReasonNotes');
      case 'most-progressed': return t('budget.duplicatesReasonProgress');
      case 'earliest-created': return t('budget.duplicatesReasonEarliest');
      default: return reason;
    }
  };

  const handleApply = async () => {
    if (!window.confirm(t('budget.duplicatesConfirm'))) return;
    try {
      setApplying(true);
      setError(null);
      const r = await applyInstallmentDedup();
      setSummary(r);
      setReport({ ...r, clusters: [] });
      onApplied?.();
    } catch (err) {
      setError(err?.message || 'error');
    } finally {
      setApplying(false);
    }
  };

  const totalRows = report
    ? report.clusters.reduce((sum, c) => sum + c.members.length, 0)
    : 0;

  return (
    <Modal
      open={show}
      onClose={onHide}
      title={t('budget.duplicatesModalTitle')}
      size="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onHide} disabled={applying}>
            {t('cancel')}
          </Button>
          {!summary && report && report.clusters.length > 0 && (
            <Button variant="accent" onClick={handleApply} disabled={applying}>
              {applying ? (
                <><Spinner size="sm" />{t('budget.duplicatesApplying')}</>
              ) : (
                t('budget.duplicatesApply')
              )}
            </Button>
          )}
        </>
      }
    >
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {summary && (
        <Alert variant="success">
          {t('budget.duplicatesAppliedSummary', {
            clusters: summary.clustersFound,
            rows: summary.rowsDeleted,
            txns: summary.transactionsRepointed,
          })}
        </Alert>
      )}

      {loading && (
        <div className={styles.loadingWrap}>
          <Spinner label={t('budget.duplicatesScanning')} />
          {t('budget.duplicatesScanning')}
        </div>
      )}

      {!loading && !summary && report && report.clusters.length === 0 && (
        <div className={styles.emptyText}>
          {t('budget.duplicatesNoneFound')}
        </div>
      )}

      {!loading && report && report.clusters.length > 0 && (
        <>
          <p className={styles.summaryText}>
            {t('budget.duplicatesFound', { count: report.clustersFound, rows: totalRows })}
          </p>
          {report.clusters.map((cluster, idx) => (
            <div key={idx} className={styles.clusterBlock}>
              <div className={styles.clusterReason}>
                {t('budget.duplicatesReason')} <em>{reasonLabel(cluster.reason)}</em>
              </div>
              <Table>
                <thead>
                  <tr>
                    <th className={styles.narrowCol}></th>
                    <th>{t('budget.merchant')}</th>
                    <th className={styles.textEnd}>{t('budget.totalAmount')}</th>
                    <th className={styles.textEnd}>{t('budget.progress')}</th>
                  </tr>
                </thead>
                <tbody>
                  {cluster.members.map(m => (
                    <tr key={m.id} className={m.isWinner ? '' : styles.loserRow}>
                      <td>
                        {m.isWinner ? (
                          <Badge variant="success">{t('budget.duplicatesWinner')}</Badge>
                        ) : (
                          <Badge variant="neutral">{t('budget.duplicatesLoser')}</Badge>
                        )}
                      </td>
                      <td>{m.merchant}</td>
                      <td className={styles.textEnd}>{formatTry(m.totalAmount)}</td>
                      <td className={styles.textEnd}>
                        {m.installmentCount - m.remainingCount}/{m.installmentCount}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          ))}
        </>
      )}
    </Modal>
  );
};

export default DuplicatesModal;
